import { CONFIRMED_APPOINTMENT_STATUSES } from 'core/appointments/domain/Appointment'
import {
  formatAppointmentDate,
  formatAppointmentStatus,
} from 'core/appointments/presenters/appointmentPresenter'
import { type Mode } from 'core/domain/Mode'
import { type TruckType } from 'core/domain/Truck'
import { type IconName } from 'core/ui/components/icon/Icon'
import { differenceInMinutes, formatDate, isPast } from 'core/utils/date'
import { round } from 'core/utils/number'

import { type TenderOffer, type TenderStop } from '../domain/TenderOffer'

const ESTIMATED_TOTAL_MINUTES = 5 * 60
export type TimeRemaining = {
  hours: number
  minutes: number
}

export function calculateTimeRemaining(expirationDate?: Date): TimeRemaining {
  if (!expirationDate) {
    return {
      hours: 0,
      minutes: 0,
    }
  }

  const now = new Date()

  if (expirationDate.getTime() <= now.getTime()) {
    return {
      hours: 0,
      minutes: 0,
    }
  }

  const totalMinutes = differenceInMinutes(expirationDate, now)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return {
    hours: Math.max(0, hours),
    minutes: Math.max(0, minutes),
  }
}

export function calculateProgressValue(expirationDate?: Date): number {
  if (!expirationDate) {
    return 0
  }

  const now = new Date()

  if (expirationDate.getTime() <= now.getTime()) {
    return 0
  }

  const totalMinutes = differenceInMinutes(expirationDate, now)
  const remaining = Math.max(0, totalMinutes)
  const progress = round((remaining * 100) / ESTIMATED_TOTAL_MINUTES, 0)

  return Math.max(0, Math.min(100, progress))
}

export function formatStopDate(date?: string): string {
  if (!date) {
    return ''
  }

  const stopDate = new Date(date)
  const month = stopDate.toLocaleDateString('en-US', { month: 'short' })
  const day = stopDate.getDate()
  return `${month} ${day}`
}

export function formatTimeRemainingString(timeRemaining: TimeRemaining): {
  hoursString: string
  minutesString: string
} {
  return {
    hoursString: timeRemaining.hours.toString().padStart(2, '0'),
    minutesString: timeRemaining.minutes.toString().padStart(2, '0'),
  }
}

export function getModeIconName(mode: Mode): IconName {
  if (mode === 'DRAY') {
    return 'truck-container'
  }
  return 'truck-dryvan'
}

export function formatEquipmentType(equipmentType: TruckType | string): string {
  return equipmentType.toUpperCase()
}

export function formatWeight(weight?: number): string {
  if (weight === undefined || weight === null) {
    return ''
  }
  return `${weight.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} lbs`
}

function formatTime(time?: string): string {
  if (!time) return ''

  return `${time.slice(0, 2)}:${time.slice(2)}`
}

function formatDateString(date?: string): string {
  if (!date) return ''
  const d = new Date(date)
  const month = d.toLocaleDateString('en-US', { month: 'short' })
  const day = d.getDate()
  return `${month} ${day}`
}

export type FormattedStopDate = {
  dateText: string
  statusText?: string
  timeText?: string
  isPendingSchedule: boolean
}

export function formatPickupDate(stop: TenderStop): FormattedStopDate {
  const appointment = stop.appointment

  if (!stop.date && !appointment) {
    return { dateText: '', isPendingSchedule: false }
  }

  let dateText = formatDateString(stop.date)

  if (
    appointment?.status &&
    CONFIRMED_APPOINTMENT_STATUSES.includes(appointment.status) &&
    appointment.scheduledTime
  ) {
    dateText = formatAppointmentDate(appointment) ?? '-'

    const timeText = formatTime(appointment.scheduledTime.start)
    return {
      dateText,
      statusText: formatAppointmentStatus(appointment.status),
      timeText,
      isPendingSchedule: false,
    }
  }

  if (appointment?.status === 'pending' && appointment.suggestedTime) {
    const timeText = formatTime(appointment.suggestedTime.start)
    return {
      dateText,
      statusText: formatAppointmentStatus(appointment.status),
      timeText,
      isPendingSchedule: true,
    }
  }

  return { dateText, isPendingSchedule: false }
}

export function formatDeliveryDate(stop: TenderStop): FormattedStopDate {
  const appointment = stop.appointment

  if (!stop.date && !appointment) {
    return { dateText: '', isPendingSchedule: false }
  }

  const suggestedDate = appointment?.suggestedDate

  let dateText = ''
  if (stop.date) {
    const startDate = new Date(stop.date)
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' })
    const startDay = startDate.getDate()

    if (suggestedDate && suggestedDate.start !== suggestedDate.end) {
      const endDate = new Date(suggestedDate.end)
      const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' })
      const endDay = endDate.getDate()
      if (startMonth === endMonth) {
        dateText = `${startMonth} ${startDay} - ${endDay}`
      } else {
        dateText = `${startMonth} ${startDay} - ${endMonth} ${endDay}`
      }
    } else {
      dateText = `${startMonth} ${startDay}`
    }
  }

  if (
    appointment?.status &&
    CONFIRMED_APPOINTMENT_STATUSES.includes(appointment.status) &&
    appointment.scheduledTime
  ) {
    dateText = formatAppointmentDate(appointment) ?? '-'

    const timeText = formatTime(appointment.scheduledTime.start)
    return {
      dateText,
      statusText: formatAppointmentStatus(appointment.status),
      timeText,
      isPendingSchedule: false,
    }
  }

  if (appointment?.status === 'pending' && appointment.suggestedTime) {
    const timeText = formatTime(appointment.suggestedTime.start)
    return {
      dateText,
      statusText: formatAppointmentStatus(appointment.status),
      timeText,
      isPendingSchedule: true,
    }
  }

  return { dateText, isPendingSchedule: false }
}

const rateTypeMap = {
  Contract: 'Contract',
  Rank: 'Contract',
  Spot: 'Spot',
}

export function getTenderResponseLabel(
  tender: TenderOffer,
  responseStatus?: 'accepted' | 'rejected' | null
) {
  const { expirationDate, rateType } = tender
  const status = responseStatus || tender.status

  const tenderString = `${rateTypeMap[rateType]} tender`

  if (status === 'accepted') return `${tenderString} accepted.`
  if (status === 'rejected') return `${tenderString} rejected.`
  if (status === 'unavailable') return `${tenderString} canceled by the shipper.`

  const isExpired = expirationDate ? isPast(expirationDate) : false

  if (isExpired)
    return `${tenderString} expired on ${formatDate(expirationDate!, 'MMM dd, yyyy - hh:mma')}.`

  return tenderString
}
